import { useEffect, useState } from "react";
import { CheckCircle2, RotateCcw, X } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import ListenButton from "../../components/ListenButton";
import GameShell, { shuffle } from "./GameShell";

export default function UnscrambleGame() {
    const [data, setData] = useState([]);
    const [idx, setIdx] = useState(0);
    const [bank, setBank] = useState([]);
    const [picked, setPicked] = useState([]);
    const [correct, setCorrect] = useState(0);
    const [done, setDone] = useState(false);

    useEffect(() => {
        api.get("/games/unscramble").then((r) => {
            setData(r.data);
            setup(r.data, 0);
        });
    }, []);

    const setup = (arr, i) => {
        const item = arr[i];
        setBank(shuffle(item.scrambled));
        setPicked([]);
    };

    const pick = (w, i) => {
        setPicked((p) => [...p, w]);
        setBank((b) => b.filter((_, idx2) => idx2 !== i));
    };

    const unpick = (i) => {
        const w = picked[i];
        setPicked((p) => p.filter((_, idx2) => idx2 !== i));
        setBank((b) => [...b, w]);
    };

    const check = async () => {
        const target = data[idx].correct.join(" ");
        const guess = picked.join(" ");
        if (guess === target) {
            const newCorrect = correct + 1;
            setCorrect(newCorrect);
            toast.success("Perfect order!");
            if (idx + 1 >= data.length) {
                setDone(true);
                try {
                    await api.post("/progress/game-score", { game: "unscramble", score: newCorrect * 10, correct: newCorrect, total: data.length });
                    toast.success(`+${newCorrect * 10} XP earned`);
                } catch {}
            } else {
                setTimeout(() => { setIdx(idx + 1); setup(data, idx + 1); }, 700);
            }
        } else {
            toast.error("Not quite — the meaning isn't right yet");
        }
    };

    const reset = () => { setIdx(0); setCorrect(0); setDone(false); setup(data, 0); };

    if (!data.length) return <GameShell title="Sentence Unscramble"><p>Loading…</p></GameShell>;

    const current = data[idx];

    return (
        <GameShell
            title="Sentence Unscramble"
            intro="Tap the words in order to make a real sentence."
            testId="unscramble-game"
            instructions="This is the Sentence Unscramble game. You will see words in the wrong order. Tap the words in the correct order to build a real sentence. Tap a placed word to remove it. When your sentence is complete, tap Check."
        >
            <div className="flex items-center justify-between mb-6">
                <p className="text-sm text-[#819888]">Sentence {idx + 1} of {data.length}</p>
                <p className="text-sm text-[#2D5D4E] font-medium" data-testid="un-score">Correct: {correct}</p>
            </div>

            {done ? (
                <div className="card-rw p-10 text-center">
                    <CheckCircle2 className="w-12 h-12 text-[#3A7D5C] mx-auto mb-4" strokeWidth={1.6} />
                    <h2 className="text-2xl font-medium mb-2" style={{ fontFamily: "Outfit" }}>All sentences solved</h2>
                    <p className="text-lg text-[#515B57] mb-6">{correct} of {data.length} correct.</p>
                    <button onClick={reset} className="inline-flex items-center gap-2 px-6 py-3 rounded-lg bg-[#2D5D4E] text-[#FAF9F6] font-medium" data-testid="un-replay">
                        <RotateCcw className="w-4 h-4" /> Play again
                    </button>
                </div>
            ) : (
                <>
                    <div className="card-rw p-6 mb-4 min-h-[80px] flex flex-wrap gap-2 items-center" data-testid="un-picked">
                        {picked.length === 0 ? (
                            <p className="text-[#819888]">Tap words below to build the sentence</p>
                        ) : (
                            picked.map((w, i) => (
                                <button key={i} onClick={() => unpick(i)} className="tile tile-correct px-4 py-2 flex items-center gap-1" data-testid={`un-picked-${i}`}>
                                    {w} <X className="w-3 h-3" />
                                </button>
                            ))
                        )}
                    </div>
                    <div className="flex flex-wrap gap-3 justify-center mb-5" data-testid="un-bank">
                        {bank.map((w, i) => (
                            <button key={i} onClick={() => pick(w, i)} className="tile px-5 py-3 text-lg" data-testid={`un-bank-${i}`}>
                                {w}
                            </button>
                        ))}
                    </div>
                    <div className="flex items-center justify-between gap-3">
                        <ListenButton text={current.correct.join(" ")} testId="un-listen" label="Listen to the right answer" size="sm" />
                        <button
                            onClick={check}
                            disabled={picked.length !== current.correct.length}
                            className="px-6 py-3 rounded-lg bg-[#2D5D4E] text-[#FAF9F6] font-medium disabled:opacity-50"
                            data-testid="un-check"
                        >
                            Check
                        </button>
                    </div>
                </>
            )}
        </GameShell>
    );
}
